import { useCallback, useEffect, useState } from 'react';
import {
  fetchMe,
  linkActivityHosts,
  linkBookingHosts,
  organizationCapabilities,
  type MeLink,
  type MeResult,
} from '../lib/api/me';

/**
 * WHAT THIS ACCOUNT MAY DO IN THE ORGANIZATION IT IS ACTING IN — for drawing,
 * never for deciding (slice 3, 2026-10-18).
 *
 * The «Tilganger» entry in the organization's settings is shown only to an
 * access manager (decision 4A). The sign-in snapshot cannot say who that is:
 * it carries role KEYS, and a role-key check in the browser is exactly the
 * coupling roles-as-data exists to remove — a custom role holding
 * `organization.access.manage` would never see its own door. So the shell
 * asks `/me`, which reports each organization's capability union, and reads
 * the current scope's entry.
 *
 * Fetched once per signed-in account, and again on `refresh()` — the shell
 * calls that when settings opens, so a right granted while the session lives
 * is seen the next time the person looks, without a poll. Null until the
 * server has answered, and null when it would not: an entry that cannot be
 * drawn is simply not drawn, and the server refuses the route regardless.
 */
export function useOrganizationCapabilities(
  accountId: string | null,
  tenantId: string | null,
  loadMe: typeof fetchMe = fetchMe,
): {
  capabilities: string[] | null;
  /** Centres this account may book at for the current club (slice 4c). */
  linkBookingHosts: MeLink[];
  /** Clubs this account may arrange activities in for the current centre (slice 4d). */
  linkActivityHosts: MeLink[];
  refresh: () => void;
} {
  const [me, setMe] = useState<MeResult | null>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    // Cleared first: a second account must never read the previous one's
    // rights while its own answer is still in flight.
    setMe(null);
    if (!accountId) return undefined;
    let live = true;
    void loadMe().then((r) => {
      if (live) setMe(r);
    });
    return () => {
      live = false;
    };
  }, [accountId, generation, loadMe]);

  const refresh = useCallback(() => setGeneration((n) => n + 1), []);
  return {
    capabilities: organizationCapabilities(me, tenantId),
    linkBookingHosts: linkBookingHosts(me, tenantId),
    linkActivityHosts: linkActivityHosts(me, tenantId),
    refresh,
  };
}
